/**
 * Whether a reply carries an answer or the assistant's failure prose.
 *
 * Every reply is HTTP 200 - the API catches each failure and answers with fixed text - so
 * this is the only thing separating an answer from an apology. It matters to rendering
 * because a failed turn also carries no citations, which is indistinguishable from an
 * answer that correctly cited nothing.
 *
 * The values are numbers because that is what the API sends: it serializes with
 * Newtonsoft.Json and registers no string-enum converter, so every enum it returns is an
 * integer. `Answered` is 0, so beware `status || fallback` - a perfectly good answer is
 * falsy.
 */
export enum AssistantResponseStatus {
  Answered = 0,
  Failed = 1,
  Conflict = 2,
  /**
   * The assistant ran out of tool rounds. The text is as far as it got.
   *
   * Its own value rather than either neighbour, because it is neither. The text is worth
   * showing - it may be most of an answer - so it is not a failure; and it is not an answer,
   * because the assistant had not finished working it out. What makes the difference matter
   * is writes: an unfinished turn used to arrive as `Answered` with a live Confirm card, so
   * the user was asked to approve a write derived from reasoning that never completed. The
   * server now stages nothing on such a turn.
   */
  Incomplete = 3,
  /**
   * The turn spent its token budget before it finished. The text is as far as it got.
   *
   * The same shape as `Incomplete` and deliberately not the same value, because the two need
   * different words. A round-limited turn met the model's own limit, and asking for one piece
   * at a time helps; this one met a number an operator chose, and no rephrasing gets past it -
   * so telling the user to try again in smaller pieces would be advice that cannot work.
   */
  Exhausted = 4
}

/** Whether a reply's text is an answer rather than the assistant's failure prose. */
export const isAnswered = (status: AssistantResponseStatus): boolean =>
  status === AssistantResponseStatus.Answered;

/** Whether the assistant stopped part-way through working this turn out. */
export const isIncomplete = (status: AssistantResponseStatus): boolean =>
  status === AssistantResponseStatus.Incomplete;

/** Whether the turn was stopped by a limit the server sets rather than by the model. */
export const isExhausted = (status: AssistantResponseStatus): boolean =>
  status === AssistantResponseStatus.Exhausted;

/**
 * Whether the assistant stopped before finishing, for either reason.
 *
 * What the two share is everything about rendering: partial text worth showing, a notice
 * beside it, and no Confirm control. What they do not share is the notice's wording.
 */
export const stoppedEarly = (status: AssistantResponseStatus): boolean =>
  isIncomplete(status) || isExhausted(status);

/**
 * Whether the reply's text is content to render rather than an error to report.
 *
 * An unfinished turn belongs on the answer path - it has citations and grounding, and the
 * partial text is useful - with a notice saying it is unfinished. Rendering it through the
 * failure path would throw all of that away and style a partial answer as an error.
 */
export const carriesAnswerText = (status: AssistantResponseStatus): boolean =>
  isAnswered(status) || stoppedEarly(status);
